import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, TextField, Button, Grid, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { getClients, addClient, updateClient, deleteClient, approveRequest, rejectRequest } from '../services/api';

function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '', phone: '' });
  const [editClient, setEditClient] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await getClients();
        setClients(data);
      } catch (err) {
        console.error('Failed to fetch clients:', err);
      }
    };
    fetchClients();
  }, []);

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      const response = await addClient(newClient);
      setClients([...clients, response]);
      setNewClient({ name: '', email: '', company: '', phone: '' });
    } catch (err) {
      alert('Failed to add client.');
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      await updateClient(editClient.id, editClient);
      setClients(clients.map(c => c.id === editClient.id ? editClient : c));
      setEditClient(null);
    } catch (err) {
      alert('Failed to update client.');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await deleteClient(clientId);
        setClients(clients.filter(c => c.id !== clientId));
        setSelectedClient(null);
      } catch (err) {
        alert('Failed to delete client.');
      }
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await approveRequest(requestId);
      setClients(clients.map(client => ({
        ...client,
        requests: client.requests.map(req => req.id === requestId ? { ...req, status: 'Approved' } : req),
      })));
    } catch (err) {
      alert('Failed to approve request.');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await rejectRequest(requestId);
      setClients(clients.map(client => ({
        ...client,
        requests: client.requests.map(req => req.id === requestId ? { ...req, status: 'Rejected' } : req),
      })));
    } catch (err) {
      alert('Failed to reject request.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Typography variant="h1" gutterBottom>Client Management</Typography>
      <Box component="form" onSubmit={handleAddClient} sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}><TextField fullWidth label="Name" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} required /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth label="Email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} required /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth label="Company" value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} /></Grid>
          <Grid item xs={12} sm={3}><Button type="submit" variant="contained">Add Client</Button></Grid>
        </Grid>
      </Box>
      <Grid container spacing={4}>
        {clients.map(client => (
          <Grid item xs={12} sm={6} md={4} key={client.id}>
            <Card onClick={() => setSelectedClient(client)}>
              <CardContent>
                <Typography variant="h3">{client.name}</Typography>
                <Typography>{client.email}</Typography>
                <Typography>Pending Requests: {client.requests.filter(r => r.status === 'Pending').length}</Typography>
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={(e) => { e.stopPropagation(); setEditClient(client); }}>Edit</Button>
                  <Button variant="contained" color="secondary" onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id); }}>Delete</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Add Table and Edit Form similarly with MUI and Framer Motion */}
    </motion.div>
  );
}

export default ClientManagement;